// Curated, static blog content for the public Zealight website.
//
// This file is the ONE source of truth for every blog surface (home preview,
// /blog listing, /blog/:slug detail). Posts are changed only by editing this
// file and redeploying - there is deliberately no public publishing path.
//
// Each post: id, slug, title, excerpt, overview, image, imageAlt, author,
// createdAt (ISO), category, tags, subParagraphs [{ title, content }], cta.
// `content` may hold several paragraphs separated by a blank line (\n\n).

import productDev1 from "../assets/product-development-1.png";
import productDev2 from "../assets/product-development-2.png";
import productDev3 from "../assets/product-development-3.png";
import productDev4 from "../assets/product-development-4.png";
import productDev5 from "../assets/product-development-5.png";
import productDev6 from "../assets/product-development-6.png";
import mobileDev from "../assets/mobile-dev-1.png";
import qualityAssurance from "../assets/quality-assurance-1.png";
import talentOutsourcing from "../assets/talent-outsourcing-1.png";
import uxResearch from "../assets/ux-research-1.png";
import itConsultancy from "../assets/it-consultancy-1.png";

const AUTHOR = "Zealight Team";

const posts = [
	{
		id: 1,
		slug: "from-idea-to-launch-good-product-development",
		title: "From Idea to Launch: What Good Product Development Actually Looks Like",
		excerpt:
			"Successful digital products do not start with code. They start with a clearly understood problem and the smallest valuable solution, built properly.",
		overview:
			"Many software projects begin with a feature list and a deadline. The ones that succeed usually begin somewhere else: with a clear understanding of who the product is for and what problem it must solve. Here is what the journey from idea to launch looks like when it is done well.",
		image: productDev1,
		imageAlt: "Zealight product development workflow illustration",
		author: AUTHOR,
		createdAt: "2026-10-03T09:00:00+01:00",
		category: "Product Development",
		tags: ["Product Development", "MVP", "Strategy", "Delivery"],
		subParagraphs: [
			{
				title: "1. Define the problem before the solution",
				content:
					"Every product starts as an opinion about a problem. Before any design or engineering begins, write that opinion down in plain language: who has the problem, how they deal with it today, and what it costs them in time, money or frustration.\n\nIf you cannot describe the problem without describing your solution, you are probably not ready to build. A clear problem statement becomes the yardstick for every later decision, including which features to leave out.",
			},
			{
				title: "2. Talk to the people who will use it",
				content:
					"User discovery does not need to be expensive. A handful of structured conversations with real customers, staff or partners will usually surface patterns that internal workshops never do. Ask about what they do now, where it breaks down and what they have already tried.\n\nThe goal is not to collect feature requests. It is to understand behaviour, so that what you build fits how people actually work.",
			},
			{
				title: "3. Turn insight into requirements and an MVP scope",
				content:
					"Requirements translate what you learned into what the product must do. Separate the must-haves, the things that deliver the core value, from the nice-to-haves that can follow later.\n\nA good minimum viable product is the smallest release that lets a real user complete the most important task end to end. It is small in scope, not low in quality. Anything you ship should be reliable, secure and usable.",
			},
			{
				title: "4. Design for clarity, then build in small increments",
				content:
					"Design is where requirements become flows, screens and interactions. Wireframes and clickable prototypes let you test ideas with users in days instead of discovering problems after months of development.\n\nEngineering then proceeds in short iterations, each producing something that works and can be reviewed. This keeps risk visible and gives stakeholders confidence because they can see progress rather than wait for a big reveal.",
			},
			{
				title: "5. Test thoroughly before you launch",
				content:
					"Quality assurance should run alongside development, not appear at the end as a gate. Functional testing, regression checks and testing across devices and browsers all reduce the chance that your first users meet your worst bugs.\n\nPlan the launch as well: data migration, support readiness, monitoring and a clear rollback path if something goes wrong.",
			},
			{
				title: "6. Treat launch as the start of learning",
				content:
					"The first release answers questions that no amount of planning can: which features people actually use, where they get stuck and what they ask for next. Measure usage, gather feedback and prioritise improvements based on evidence.\n\nProducts that grow well are rarely the ones with the longest initial feature list. They are the ones whose teams keep learning and keep shipping small, well-tested improvements.",
			},
			{
				title: "In summary",
				content:
					"Good product development is disciplined rather than glamorous: understand the problem, scope the smallest valuable solution, design and build it carefully, test it properly and keep improving it after launch. Skipping the early steps rarely saves time. It usually just moves the cost to later, when it is more expensive to fix.",
			},
		],
		cta: {
			text: "If you are planning a new digital product and need help turning the idea into a practical delivery plan, Zealight can help you move from discovery through design, development and launch.",
			label: "Talk to us about your product",
			href: "/contact",
		},
	},
	{
		id: 2,
		slug: "before-you-build-an-app-10-questions",
		title: "Before You Build an App: 10 Questions Every Business Should Answer",
		excerpt:
			"A practical checklist for companies considering a new web or mobile application: users, problem, scope, platform, budget, data, integrations, security and ownership.",
		overview:
			"Commissioning software is a significant decision. Teams that answer a handful of questions before the first design is drawn tend to build better products, with fewer surprises. Use this checklist in your next planning conversation, whether you build in-house or with a partner.",
		image: productDev2,
		imageAlt: "Planning a new app: requirements and product scoping",
		author: AUTHOR,
		createdAt: "2026-10-03T08:50:00+01:00",
		category: "Product Development",
		tags: ["Planning", "Mobile Apps", "Web Apps", "Checklist"],
		subParagraphs: [
			{
				title: "1. Who exactly is this for?",
				content:
					"\"Our customers\" is not an answer. Name the specific groups who will use the application and what each is trying to achieve. An app for field staff on poor connectivity is a very different product from an app for office managers on laptops.",
			},
			{
				title: "2. What business problem does it solve?",
				content:
					"Link the application to an outcome you can recognise: fewer manual steps, faster onboarding, better visibility, a new revenue channel. If the problem is unclear, the project will drift and every opinion about features will carry equal weight.",
			},
			{
				title: "3. What must it do on day one, and what can wait?",
				content:
					"List the capabilities without which the product has no value, then list everything else separately. This single exercise often cuts the first release scope substantially and protects your timeline.",
			},
			{
				title: "4. Which platform makes sense?",
				content:
					"Mobile app, responsive website, web application or a combination? The right choice depends on where your users are, whether you need device features such as camera or notifications, and how you will distribute and update the product. We cover this in more detail in our article on choosing between mobile and web.",
			},
			{
				title: "5. What are the budget and timeline constraints?",
				content:
					"Be honest about both, and about which one matters more. A fixed date and a fixed budget usually means flexible scope. Sharing realistic constraints early lets a delivery team recommend a phased plan instead of overpromising.",
			},
			{
				title: "6. What data will the system hold?",
				content:
					"Identify what data you will collect, where it comes from, who may see it and how long you need to keep it. Data decisions affect design, compliance obligations and long-term cost far more than most teams expect.",
			},
			{
				title: "7. What does it need to integrate with?",
				content:
					"Payment providers, accounting tools, CRMs, identity systems, existing databases. Integrations are a common source of hidden effort, so list them early and check what interfaces those systems actually offer.",
			},
			{
				title: "8. How will you handle security and privacy?",
				content:
					"Decide what sensitive information the application will touch and what standards apply to your industry. Authentication, access control, encryption and secure hosting should be designed in from the start. They are very hard to bolt on at the end.",
			},
			{
				title: "9. How will success be measured?",
				content:
					"Pick a few measures you can track after launch: tasks completed, time saved, active users, error rates. Without them, you will only be able to judge the product by opinion.",
			},
			{
				title: "10. Who owns it, and who will maintain it?",
				content:
					"Confirm that you will own the source code, designs and data, and decide who will fix bugs, apply security updates and add features after launch. Software that nobody maintains slowly becomes a liability.",
			},
		],
		cta: {
			text: "Not sure how your answers translate into a plan? Our team can help you work through these questions in a short consultation and shape them into a realistic roadmap.",
			label: "Book a planning conversation",
			href: "/contact",
		},
	},
	{
		id: 3,
		slug: "mvp-vs-full-product-what-to-build-first",
		title: "MVP vs Full Product: What Should Your Business Build First?",
		excerpt:
			"An MVP is not a rushed prototype. Learn how to find the core value, prioritise features and know when a fuller first release makes more sense.",
		overview:
			"The term minimum viable product is often misunderstood. Some take it as permission to ship something fragile; others avoid it for fear of launching something too small. Used properly, an MVP is a focused, well-built first release designed to learn quickly.",
		image: productDev3,
		imageAlt: "Prioritising features for a minimum viable product",
		author: AUTHOR,
		createdAt: "2026-10-03T08:40:00+01:00",
		category: "Product Strategy",
		tags: ["MVP", "Product Strategy", "Prioritisation", "Startups"],
		subParagraphs: [
			{
				title: "What an MVP really is",
				content:
					"An MVP is the smallest version of a product that lets real users achieve a real goal, and lets you learn whether the idea works. The word viable matters as much as minimum. If users cannot complete the core task reliably, you have not tested your idea, you have only tested your tolerance for bugs.",
			},
			{
				title: "What an MVP is not",
				content:
					"It is not a throwaway demo, and it is not an excuse to skip design, testing or security. It is also not a collection of half-finished features. It is a smaller number of features, done properly.\n\nBuilding a poor-quality MVP is often a false economy, because the team ends up rebuilding it before it can grow.",
			},
			{
				title: "Find the core value",
				content:
					"Ask what single job the product must do better than the alternatives. For a booking tool it might be \"let a customer reserve a slot in under a minute\". Everything in the first release should either support that job or be necessary to operate the product safely, such as sign-in and basic admin.",
			},
			{
				title: "Prioritise ruthlessly",
				content:
					"Sort candidate features into those that are essential, valuable but deferrable, and nice to have. A simple test: if this were missing at launch, would the core task still work? If yes, it can probably wait.\n\nWrite down what you are deliberately not building. It makes the trade-offs visible to everyone.",
			},
			{
				title: "Build foundations that can grow",
				content:
					"A small scope does not mean a disposable foundation. Choose sensible architecture, keep the data model clean and write maintainable code, so that the second and third releases build on the first rather than replacing it.",
			},
			{
				title: "Create a feedback loop",
				content:
					"Decide before launch how you will learn: user interviews, analytics on key actions, support requests, short surveys. Schedule time to review what you have learned and let it shape the next iteration.",
			},
			{
				title: "When a fuller first release makes sense",
				content:
					"Sometimes the minimum is larger than people expect. Regulated sectors, products that replace an existing critical system, and platforms that need two sides of a marketplace to be useful may all need a broader first release. The principle still holds: build what is required for the product to be trustworthy and useful, and be deliberate about the rest.",
			},
		],
		cta: {
			text: "Deciding what belongs in version one is one of the most valuable conversations in any project. If you would like an experienced second opinion on your scope, we would be glad to help.",
			label: "Discuss your MVP with Zealight",
			href: "/contact",
		},
	},
	{
		id: 4,
		slug: "why-user-experience-makes-or-breaks-digital-products",
		title: "Why User Experience Can Make or Break a Digital Product",
		excerpt:
			"UX is not decoration. It is how easily people can understand, trust and complete tasks in your product, and it directly influences conversion and retention.",
		overview:
			"Two products can offer similar features and perform very differently, because one is easy to use and the other is not. User experience is the difference, and it has measurable consequences for any business that depends on digital channels.",
		image: uxResearch,
		imageAlt: "UX research and usability testing for a digital product",
		author: AUTHOR,
		createdAt: "2026-10-03T08:30:00+01:00",
		category: "UX Research",
		tags: ["UX", "User Research", "Usability", "Accessibility"],
		subParagraphs: [
			{
				title: "UX and visual design are related, not identical",
				content:
					"A beautiful interface can still be confusing. Visual design shapes how a product looks and feels; user experience covers whether people can find what they need, understand what to do next and recover when something goes wrong. The best products treat both seriously, but when forced to choose, clarity beats decoration.",
			},
			{
				title: "Friction costs you users",
				content:
					"Every unnecessary step, ambiguous label or surprising error asks the user to work harder. Some will push through. Many will not. Long forms, hidden navigation, unclear pricing and confusing checkout flows are common sources of drop-off.\n\nReducing friction is often less about adding features than removing obstacles.",
			},
			{
				title: "Navigation and clarity",
				content:
					"People should be able to answer three questions on any screen: where am I, what can I do here, and how do I get back? Consistent navigation, plain-language labels and a clear visual hierarchy make those answers obvious.",
			},
			{
				title: "Onboarding sets the tone",
				content:
					"The first few minutes decide whether a new user sees value. Good onboarding gets people to a meaningful first result quickly, explains only what is needed and avoids front-loading settings and tutorials that can be offered later.",
			},
			{
				title: "Accessibility is part of good UX",
				content:
					"Sufficient colour contrast, keyboard support, readable text sizes, meaningful labels and clear error messages help everyone, including people with disabilities, people on small screens and people in bright sunlight. Considering accessibility early also widens your audience.",
			},
			{
				title: "Research and testing replace assumptions",
				content:
					"Teams close to a product often cannot see its problems. Watching a few real users attempt key tasks, or running simple usability tests on a prototype, reveals confusion that analytics alone will not explain.\n\nTreat your design decisions as hypotheses and test the important ones before committing to development.",
			},
			{
				title: "Conversion and retention follow",
				content:
					"When tasks are easy and the product feels trustworthy, more people complete sign-ups and purchases, and more of them return. UX is not a separate topic from business performance. It is one of the main ways products earn it.",
			},
		],
		cta: {
			text: "If users are dropping off or your product feels harder to use than it should, a focused UX review can show you where to start. Talk to the Zealight team about research and design support.",
			label: "Request a UX review",
			href: "/contact",
		},
	},
	{
		id: 5,
		slug: "mobile-app-or-web-app-choosing-the-right-platform",
		title: "Mobile App or Web App? How to Choose the Right Platform",
		excerpt:
			"Native app, responsive website, web application or progressive web app? A balanced guide to choosing the platform that fits your audience and budget.",
		overview:
			"There is no universally correct platform. The right choice depends on your users, what the product needs to do and how you plan to maintain it. Here are the main options and the questions that help you decide.",
		image: mobileDev,
		imageAlt: "Mobile and web application development",
		author: AUTHOR,
		createdAt: "2026-10-03T08:20:00+01:00",
		category: "Mobile App Development",
		tags: ["Mobile Apps", "Web Apps", "PWA", "Platform Strategy"],
		subParagraphs: [
			{
				title: "The main options",
				content:
					"A responsive website adapts to any screen and is the simplest way to be present everywhere. A web application adds logged-in, interactive functionality in the browser. A progressive web app (PWA) adds installability and some offline behaviour to a web app. A native or cross-platform mobile app is installed from an app store and can use the full capabilities of the device.\n\nMany organisations end up with a combination.",
			},
			{
				title: "Start from audience behaviour",
				content:
					"Where do your users actually spend time? Customers who use your service daily on their phones may justify an app. Occasional users who arrive from a search result or a shared link will probably prefer not to install anything. Staff using a tool on desks may be best served by a web application.",
			},
			{
				title: "Device features and offline needs",
				content:
					"If you need deep access to the camera, Bluetooth, background location or reliable push notifications, a mobile app is often the better fit, although modern browsers support more than they used to. If users work in low-connectivity areas, consider how much must work offline and design for it deliberately, whichever platform you choose.",
			},
			{
				title: "Distribution and update cycles",
				content:
					"Web applications update instantly for everyone. Mobile apps go through app-store review and rely on users to update, which affects how quickly you can fix problems and how long you must support older versions. App stores also bring discoverability and a sense of legitimacy, but also rules and fees.",
			},
			{
				title: "Budget and maintenance",
				content:
					"Supporting several platforms multiplies design, testing and maintenance effort. Cross-platform frameworks can reduce duplication, and a well-built web application can cover many needs at a lower cost. Whatever you choose, plan for the ongoing work: operating system updates, security fixes and new devices.",
			},
			{
				title: "A practical way to decide",
				content:
					"Write down your core user journeys, then mark which ones need device features, offline use or app-store presence. If few do, begin with a strong responsive web application and add a mobile app when the evidence supports it. If most do, start with mobile for the audience that matters most.",
			},
		],
		cta: {
			text: "Weighing up platforms for your next product? Zealight builds both web and mobile solutions and can help you choose the approach that fits your users and your budget.",
			label: "Explore our services",
			href: "/services",
		},
	},
	{
		id: 6,
		slug: "software-quality-assurance-is-more-than-finding-bugs",
		title: "Software Quality Assurance Is More Than Finding Bugs",
		excerpt:
			"Modern QA starts before a line of code is written and continues after release. Here is what it covers and why late defects cost so much more.",
		overview:
			"Quality assurance is often pictured as someone clicking through an app at the end of a project. In practice, good QA is a continuous discipline that reduces risk throughout delivery and gives teams real confidence to release.",
		image: qualityAssurance,
		imageAlt: "Quality assurance testing of software across devices",
		author: AUTHOR,
		createdAt: "2026-10-03T08:10:00+01:00",
		category: "Quality Assurance",
		tags: ["QA", "Testing", "Release Quality", "Regression"],
		subParagraphs: [
			{
				title: "Start with the requirements",
				content:
					"Many defects are born in unclear or contradictory requirements. A QA review of requirements and designs, asking what happens in edge cases and what \"done\" means, catches problems when they are cheapest to fix.",
			},
			{
				title: "Plan the testing",
				content:
					"A test plan identifies what will be tested, how, on which devices and browsers, and with what data. It focuses effort on the areas of greatest business risk, such as payments, sign-in and data integrity, instead of treating everything equally.",
			},
			{
				title: "Functional and regression testing",
				content:
					"Functional testing confirms the software does what it should. Regression testing confirms that new changes have not broken existing behaviour. Automating the most repetitive checks makes regression testing fast and repeatable, freeing people to explore more subtle problems.",
			},
			{
				title: "Cross-device and cross-browser coverage",
				content:
					"Your users do not all share one screen size, one operating system or one browser. Testing on a realistic range of devices catches layout problems, input quirks and compatibility issues that never appear on the developer's own machine.",
			},
			{
				title: "Usability and performance awareness",
				content:
					"A feature that technically works but is confusing or slow is still a quality problem. QA can flag usability concerns and check that pages load and respond acceptably under realistic conditions, before users find out for you.",
			},
			{
				title: "The business cost of late defects",
				content:
					"A bug found during design costs a conversation. Found in development, it costs some developer time. Found after release, it can cost support effort, lost customers, emergency fixes and damage to trust. For issues involving security or data, the consequences can be more serious. Catching problems early is simply cheaper.",
			},
			{
				title: "Make quality continuous",
				content:
					"The strongest teams build quality checks into every stage: reviews, automated tests that run on each change, structured release checklists and post-release monitoring. The outcome is not a bug-free product, which does not exist, but predictable releases and informed risk decisions.",
			},
		],
		cta: {
			text: "Need an independent quality review before your next release? Talk to the Zealight team about your software and release goals.",
			label: "Talk to our QA team",
			href: "/contact",
		},
	},
	{
		id: 7,
		slug: "seven-warning-signs-your-business-software-needs-a-rebuild",
		title: "7 Warning Signs Your Business Software Needs a Rebuild",
		excerpt:
			"Downtime, slow performance, manual workarounds and security worries can all signal ageing software. Sometimes you need a rebuild; often targeted modernisation is enough.",
		overview:
			"Business software rarely fails all at once. It degrades: a little slower each year, a little harder to change, a little more dependent on workarounds. These seven signs suggest it is time to take a serious look at your system.",
		image: itConsultancy,
		imageAlt: "IT consultancy review of business systems",
		author: AUTHOR,
		createdAt: "2026-10-03T08:00:00+01:00",
		category: "IT Consultancy",
		tags: ["Modernisation", "Legacy Systems", "Software Rebuild", "IT Strategy"],
		subParagraphs: [
			{
				title: "1. Frequent downtime",
				content:
					"If outages and unexplained errors have become normal, the cost is no longer just technical. Staff lose time, customers lose confidence and the team spends its energy on firefighting instead of improvement.",
			},
			{
				title: "2. It has become slow",
				content:
					"Slow pages, long-running reports and screens that freeze are usually symptoms of growth the system was never designed for. Sometimes the fix is tuning; sometimes it points to structural limits in the data model or architecture.",
			},
			{
				title: "3. Security concerns",
				content:
					"Unsupported frameworks, unpatched servers, weak authentication and unclear access control all increase risk. If nobody can confidently say how the system is protected, that uncertainty itself is a warning sign.",
			},
			{
				title: "4. Staff rely on manual workarounds",
				content:
					"Spreadsheets that sit alongside the system, copy-and-paste between screens and tasks that only one person knows how to do indicate that the software no longer matches the way the business works.",
			},
			{
				title: "5. Integrations are painful",
				content:
					"When connecting to a payment provider, a partner or a new internal tool requires heroic effort, the system probably lacks clean interfaces. This limits what the business can do next.",
			},
			{
				title: "6. The code is hard to change",
				content:
					"If small changes take weeks, fixes cause new bugs and only one developer understands the codebase, then the software has accumulated heavy technical debt. Missing documentation and tests make every change risky.",
			},
			{
				title: "7. The mobile experience is poor",
				content:
					"Customers and staff increasingly expect to work from their phones. A system that is barely usable on a small screen limits access and productivity.",
			},
			{
				title: "Rebuild or modernise?",
				content:
					"A rebuild is not always the answer. A structured assessment can show whether the foundations are sound enough to modernise in stages, for example by upgrading dependencies, replacing the worst-performing module, adding an API layer or improving the interface, while the business keeps running.\n\nA rebuild makes sense when the architecture fundamentally blocks your goals, when the technology is end-of-life, or when the cost of continuing to patch exceeds the cost of starting again with better foundations. Whichever route you choose, begin with an honest assessment and a phased plan.",
			},
		],
		cta: {
			text: "Not sure whether your system needs a rebuild or a targeted upgrade? Zealight can review your software and recommend a pragmatic path forward.",
			label: "Ask for a system assessment",
			href: "/contact",
		},
	},
	{
		id: 8,
		slug: "how-to-choose-the-right-technology-partner",
		title: "How to Choose the Right Technology Partner for Your Business",
		excerpt:
			"A practical set of criteria for evaluating software partners: discovery quality, communication, technical depth, QA, security, ownership and post-launch support.",
		overview:
			"Choosing who builds your software is one of the most consequential decisions in a digital project. Price and portfolio matter, but the following criteria tend to predict whether a partnership will actually work.",
		image: productDev4,
		imageAlt: "Evaluating a technology partner for a software project",
		author: AUTHOR,
		createdAt: "2026-10-03T07:50:00+01:00",
		category: "Technology Partnerships",
		tags: ["Vendor Selection", "Outsourcing", "Software Projects", "Due Diligence"],
		subParagraphs: [
			{
				title: "They try to understand your business first",
				content:
					"A good partner asks about your customers, constraints and goals before proposing solutions. If the first meeting is mostly a pitch about their technology, be cautious. The quality of discovery questions is often the best early indicator of the quality of the eventual product.",
			},
			{
				title: "Communication is clear and consistent",
				content:
					"Look for plain language, regular updates, a named point of contact and honesty about risks. Ask how they handle disagreements and changes in scope. Problems are inevitable in software; how they are communicated is what matters.",
			},
			{
				title: "Technical competence and product thinking",
				content:
					"Check relevant experience, but also ask how they approach trade-offs. A strong team can explain why a design or technology is right for your situation, and can challenge a feature that will not serve your users. Delivery capability without product thinking can produce software that is correct but unused.",
			},
			{
				title: "Process, documentation and QA",
				content:
					"Ask how requirements are captured, how progress is shown, what documentation you receive and how testing is done. Dedicated quality assurance, automated checks where sensible and a defined release process are marks of a mature team.",
			},
			{
				title: "Security and data handling",
				content:
					"Ask how they protect your code, credentials and data during the project, and how they approach secure development. A thoughtful answer should cover access control, environments, dependency management and incident handling.",
			},
			{
				title: "Ownership and exit",
				content:
					"Agree in writing that you own the source code, designs and associated intellectual property. Make sure you could hand the project to another team without starting over, with documented set-up, access to repositories and no hidden dependencies.",
			},
			{
				title: "Post-launch support",
				content:
					"Launch is the beginning of a product's life. Ask what support, maintenance and improvement options exist, how issues are prioritised and what response expectations apply.",
			},
			{
				title: "Transparency in everything else",
				content:
					"Fair pricing logic, honest estimates, visible progress and willingness to say \"we don't know yet\" are all signs of a partner worth trusting. References from similar projects can help, and a small paid pilot is often a better test than a long sales process.",
			},
		],
		cta: {
			text: "These are the standards we hold ourselves to at Zealight. If you are evaluating partners, we would welcome the chance to be assessed against them.",
			label: "Start a conversation",
			href: "/contact",
		},
	},
	{
		id: 9,
		slug: "building-software-that-grows-with-your-business",
		title: "Building Software That Can Grow With Your Business",
		excerpt:
			"Scalability is mostly about good decisions made early: clean data, sensible architecture, security, monitoring and avoiding both technical debt and over-engineering.",
		overview:
			"Software that works for fifty users can struggle with five thousand, and software built for a vision of millions can be needlessly expensive today. Building for growth means making sensible decisions now that keep future options open.",
		image: productDev5,
		imageAlt: "Scalable software architecture for a growing business",
		author: AUTHOR,
		createdAt: "2026-10-03T07:40:00+01:00",
		category: "Engineering",
		tags: ["Scalability", "Architecture", "Technical Debt", "APIs"],
		subParagraphs: [
			{
				title: "Start with sensible architecture",
				content:
					"Good architecture separates concerns so that parts of the system can change independently. It does not have to be elaborate. A well-structured application with clear boundaries between interface, business rules and data is far easier to extend than a tangled one.",
			},
			{
				title: "Get the data model right",
				content:
					"Data outlives code. A clean, well-thought-out data model makes reporting, integration and future features much easier. Fixing a poorly designed data model after years of use is one of the most painful kinds of rework.",
			},
			{
				title: "Design clear APIs",
				content:
					"Well-defined, documented interfaces let your web app, mobile app and partner systems share the same business logic and make it easier to add new channels later.",
			},
			{
				title: "Treat security as a foundation",
				content:
					"Authentication, authorisation, input validation, encryption and secure configuration are far easier to include from the start than to retrofit. As systems grow, so does the value of the data they hold, and the consequences of a breach.",
			},
			{
				title: "Plan for performance, measure it, and fix what matters",
				content:
					"Avoid guessing where bottlenecks will be. Build with reasonable efficiency, measure real usage and optimise the parts that actually slow people down. Caching, database indexing and background processing are typical tools when the evidence calls for them.",
			},
			{
				title: "Deployment and monitoring",
				content:
					"Repeatable deployments, separate environments and good monitoring let you release changes safely and notice problems before users do. Logs, alerts and basic health dashboards are inexpensive insurance.",
			},
			{
				title: "Documentation and technical debt",
				content:
					"Short, current documentation of how the system fits together and how to run it saves enormous time as teams change. Technical debt is not always bad. Sometimes a shortcut is the right trade-off, but it should be recorded and repaid deliberately before it compounds.",
			},
			{
				title: "Avoid premature over-engineering",
				content:
					"The opposite risk is building complex infrastructure for scale you may never reach. Aim for the simplest architecture that meets today's needs and does not block tomorrow's. You can grow it as demand and evidence justify.",
			},
		],
		cta: {
			text: "Whether you are shaping a new product or preparing an existing one for growth, Zealight can review your architecture and help you plan the next stage of scale.",
			label: "Plan for growth with us",
			href: "/contact",
		},
	},
	{
		id: 10,
		slug: "when-should-you-outsource-software-talent",
		title: "When Should You Outsource Software Talent?",
		excerpt:
			"Outsourcing talent can add specialist skills and capacity quickly, but it is not right for every situation. Here is when it works well and when it does not.",
		overview:
			"Hiring full-time developers is not always the fastest or most sensible route. Talent outsourcing, adding external specialists to your team, can fill gaps effectively when used for the right reasons and managed well.",
		image: talentOutsourcing,
		imageAlt: "Outsourced software talent working alongside an internal team",
		author: AUTHOR,
		createdAt: "2026-10-03T07:30:00+01:00",
		category: "Talent Outsourcing",
		tags: ["Talent Outsourcing", "Hiring", "Team Augmentation", "Delivery"],
		subParagraphs: [
			{
				title: "When it makes sense",
				content:
					"Outsourcing tends to work best in a few common situations: you need a specialist skill your team lacks, you need extra capacity for a defined period, you want to deliver a project faster than hiring would allow, or you want a dedicated team for a specific piece of work.",
			},
			{
				title: "Specialist skills on demand",
				content:
					"Needs such as mobile development, quality engineering or user research can be intermittent. Bringing in experienced people for the duration of a project avoids long recruitment cycles and permanent cost for short-term needs.",
			},
			{
				title: "Working alongside internal teams",
				content:
					"External engineers work best when treated as part of the team: shared tools, shared goals, access to the people who know the business and clear ownership of the work. Integration, not isolation, is what makes this model succeed.",
			},
			{
				title: "Cost predictability",
				content:
					"A defined scope and engagement term can make costs easier to plan than recruitment, onboarding and employment overheads. It is important to compare like with like and to be clear on what is included.",
			},
			{
				title: "Set management expectations",
				content:
					"Outsourced talent still needs direction. Define goals, deliverables, communication rhythm, code review practices and how knowledge will be handed over. Poorly managed outsourcing disappoints regardless of the quality of the people.",
			},
			{
				title: "When it may not be the best choice",
				content:
					"If the work is the core intellectual property of a company and requires deep, long-term domain knowledge, building an internal team may be wiser. Outsourcing may also disappoint where requirements are completely undefined, where nobody has time to manage the engagement, or where the organisation expects an external team to fix cultural or planning problems by itself.",
			},
			{
				title: "Choosing a good fit",
				content:
					"Look at the experience of the people who will actually do the work, how they are vetted, how continuity is handled and how knowledge transfer works. A short trial engagement is often a sensible way to test fit.",
			},
		],
		cta: {
			text: "Need skilled engineers, designers or QA specialists to strengthen your team? Zealight can help you scope the right engagement for your goals.",
			label: "Discuss your team needs",
			href: "/contact",
		},
	},
	{
		id: 11,
		slug: "why-product-discovery-saves-time-and-money",
		title: "Why Product Discovery Saves Time and Money Before Development Starts",
		excerpt:
			"Discovery turns assumptions into evidence. Stakeholder interviews, user research, journey mapping and prototypes reduce risk before significant money is spent.",
		overview:
			"It is tempting to start building as soon as an idea is approved. Product discovery, a short, structured phase of research and planning, usually makes the build itself faster, clearer and more likely to succeed.",
		image: productDev6,
		imageAlt: "Product discovery workshop and journey mapping",
		author: AUTHOR,
		createdAt: "2026-10-03T07:20:00+01:00",
		category: "Product Discovery",
		tags: ["Product Discovery", "UX Research", "Requirements", "Risk"],
		subParagraphs: [
			{
				title: "What discovery is for",
				content:
					"Discovery answers the questions that are risky to guess: Who is this for? What do they really need? What is feasible with our systems and constraints? What should we build first? It replaces assumptions with evidence while changes are still cheap.",
			},
			{
				title: "Stakeholder interviews",
				content:
					"Speaking with the people who sponsor, operate or depend on the product exposes differing expectations early. Misalignment discovered in week two is an inconvenience. Discovered in month six, it is an expensive rewrite.",
			},
			{
				title: "User research and journey mapping",
				content:
					"Interviews and observation with real users show how they currently achieve their goals. Mapping their journey highlights pain points and moments that matter, and helps the team focus on what will make a difference.",
			},
			{
				title: "Requirements and technical feasibility",
				content:
					"Clear, prioritised requirements, plus an early look at integrations, data sources and infrastructure, prevent unpleasant discoveries during development, such as a system that cannot provide the data the product depends on.",
			},
			{
				title: "Prioritisation and prototypes",
				content:
					"Once options are understood, prioritise by value and effort. Clickable prototypes then let stakeholders and users react to something concrete, which is far cheaper than reacting to finished software.",
			},
			{
				title: "Identify risks and define success",
				content:
					"List the biggest uncertainties, whether technical, commercial or behavioural, and decide how to test each. Agree how success will be measured, so the team and the business share the same finish line.",
			},
			{
				title: "How much discovery is enough?",
				content:
					"It should be proportionate. A small internal tool may need only a few days; a complex multi-user platform justifies longer. The aim is not paperwork but confidence, and a plan the whole team believes in.",
			},
		],
		cta: {
			text: "Considering a significant build? A short discovery engagement with Zealight can clarify scope, risk and priorities before you commit to a full development budget.",
			label: "Plan a discovery phase",
			href: "/contact",
		},
	},
	{
		id: 12,
		slug: "launching-is-only-the-beginning-improving-a-product-after-release",
		title: "Launching Is Only the Beginning: How to Improve a Digital Product After Release",
		excerpt:
			"The first release answers questions no plan could. Use feedback, analytics, support trends and steady QA to keep improving, and keep the product secure.",
		overview:
			"Many teams treat launch as the finish line. In reality, it is where the most valuable learning begins. Products that keep improving after release usually follow a few simple habits.",
		image: productDev1,
		imageAlt: "Improving a digital product after launch with feedback and analytics",
		author: AUTHOR,
		createdAt: "2026-10-03T07:10:00+01:00",
		category: "Product Development",
		tags: ["Post-Launch", "Analytics", "Roadmap", "Maintenance"],
		subParagraphs: [
			{
				title: "Listen to feedback",
				content:
					"Gather feedback through user conversations, in-app prompts, reviews and your support team. Look for patterns rather than reacting to every individual request. When several people describe the same difficulty in different words, you have found something important.",
			},
			{
				title: "Use analytics honestly",
				content:
					"Measure the actions that reflect real value, such as completed tasks, repeat usage and drop-off points, rather than vanity numbers. Analytics tell you what is happening; conversations help you understand why.",
			},
			{
				title: "Treat bug reports and support trends as data",
				content:
					"Recurring support questions point to confusing design or missing features. A clear process for reporting, triaging and fixing bugs, with severity levels everyone understands, keeps problems from piling up.",
			},
			{
				title: "Keep quality assurance running",
				content:
					"Every new release can break something that used to work. Regression testing, ideally with automated checks for key journeys, protects users and lets the team ship improvements with confidence.",
			},
			{
				title: "Prioritise improvements deliberately",
				content:
					"Weigh each candidate improvement by its value to users and the business against its effort and risk. A visible roadmap, reviewed regularly, keeps the team aligned and sets expectations for stakeholders.",
			},
			{
				title: "Watch performance and security",
				content:
					"Monitor speed and availability as usage grows. Apply security updates to frameworks, libraries and servers promptly, because unmaintained software becomes vulnerable over time. This ongoing work is not glamorous, but it protects both users and reputation.",
			},
			{
				title: "Iterate in small, safe steps",
				content:
					"Frequent small releases are easier to test, easier to roll back and give faster feedback than occasional large ones. Over time, this steady rhythm compounds into a much better product.",
			},
		],
		cta: {
			text: "Have a product that is live but not performing as you hoped? Zealight can help you review feedback, quality and performance and plan the next phase of improvements.",
			label: "Plan your next release",
			href: "/contact",
		},
	},
];

export const blogPosts = [...posts].sort(
	(a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
);

export const getPostBySlug = (slug) => blogPosts.find((post) => post.slug === slug) || null;

export const getLatestPosts = (count) => blogPosts.slice(0, count);
